import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEnvelope } from 'react-icons/fa';

const SECTIONS = [
  {
    id: 'info-collect',
    title: '1. Information We Collect',
    content: [
      {
        sub: '1.1 Information Provided by You',
        items: [
          { label: 'Personal Identifiers', text: 'Full name, email address, mobile number, and date of birth.' },
          { label: 'Professional Profile', text: 'Resume/CV, work history, educational qualifications, skills, certifications, and portfolio links.' },
          { label: 'Job Preferences', text: 'Job preferences, expected salary, preferred locations, and work type (remote/hybrid/on-site).' },
          { label: 'Recruiter/Employer Data', text: 'Company name, designation, GST details (if applicable), and recruiter credentials.' },
          { label: 'Media & Documents', text: 'Profile photo and identity verification documents (where required).' },
        ],
      },
      {
        sub: '1.2 Information Collected Automatically',
        items: [
          { label: 'Technical Data', text: 'IP address, browser type, operating system, and device identifiers.' },
          { label: 'Usage & Behavioral Data', text: 'Pages visited, search queries made, job listings clicked, and time spent on platform.' },
          { label: 'Session & Tracking Data', text: 'Cookie data, session tokens, and similar tracking identifiers.' },
          { label: 'Location Data', text: 'Approximate geographic location based on IP address.' },
        ],
      },
      {
        sub: '1.3 Information from Third Parties',
        bullets: [
          'Login credentials shared via Google, LinkedIn, or other OAuth providers (if you use third-party login).',
          'Public profile data from professional networks when you choose to import your profile.',
          'Background or reference check information from authorized third-party providers (with your consent).',
        ],
      },
    ],
  },
  {
    id: 'how-we-use',
    title: '2. How We Use Your Information',
    items: [
      { label: 'Core Service', text: 'Facilitating job matching between seekers and recruiters based on profile compatibility.' },
      { label: 'Communications', text: 'Sending job alerts, application updates, and recruiter messages via email, SMS, and in-app notifications.' },
      { label: 'Platform Improvement', text: 'Improving platform features, conducting A/B testing, and analyzing usage patterns.' },
      { label: 'Security & Fraud Prevention', text: 'Verifying user identity, preventing fraud, and protecting platform integrity.' },
      { label: 'Legal Compliance', text: 'Complying with legal obligations, court orders, or regulatory requirements.' },
      { label: 'Personalization', text: 'Displaying targeted job recommendations and (if applicable) relevant advertisements.' },
      { label: 'Analytics & Research', text: 'Generating aggregated, anonymized industry reports and salary insights.' },
    ],
  },
  {
    id: 'legal-basis',
    title: '3. Legal Basis for Processing',
    bullets: [
      'Consent: Where you have explicitly provided consent (e.g., for marketing communications).',
      'Contractual Necessity: To deliver the services you have signed up for.',
      'Legitimate Interest: For fraud prevention, platform security, and service improvement.',
      'Legal Obligation: To comply with applicable Indian laws and regulations.',
    ],
  },
  {
    id: 'data-sharing',
    title: '4. Data Sharing and Disclosure',
    content: [
      { sub: '4.1 With Employers and Recruiters', text: 'When you apply for a job or make your profile searchable, your professional information (name, resume, contact details, skills) will be visible to relevant recruiters and employers on the platform. You control the visibility of your profile through your privacy settings.' },
      { sub: '4.2 With Service Providers', text: 'We engage trusted third-party vendors for services such as cloud hosting, email delivery, analytics, payment processing, and customer support. These providers are contractually bound to process your data only on our instructions and in accordance with this Policy.' },
      { sub: '4.3 With Legal Authorities', text: 'We may disclose your data to government bodies, law enforcement agencies, or courts when required by law, court order, or to protect the rights and safety of our users and the platform.' },
      { sub: '4.4 Business Transfers', text: 'In the event of a merger, acquisition, or sale of assets, your data may be transferred to the successor entity. We will notify you of any such change.' },
      {
        sub: '4.5 What We Do Not Do',
        bullets: [
          'We do not sell your personal data to third-party advertisers.',
          'We do not share your data with unauthorized entities outside our disclosed partner network.',
          'We do not transfer your sensitive personal data outside India without adequate safeguards.',
        ],
      },
    ],
  },
  {
    id: 'your-rights',
    title: '5. Your Rights as a User',
    table: [
      { right: 'Right to Access', meaning: 'Request a copy of the personal data we hold about you.' },
      { right: 'Right to Rectification', meaning: 'Ask us to correct inaccurate or incomplete data.' },
      { right: 'Right to Deletion', meaning: 'Request erasure of your data (subject to legal obligations).' },
      { right: 'Right to Portability', meaning: 'Receive your data in a structured, machine-readable format.' },
      { right: 'Right to Object', meaning: 'Object to processing based on legitimate interests.' },
      { right: 'Right to Withdraw Consent', meaning: 'Withdraw previously given consent at any time.' },
      { right: 'Right to Restrict Processing', meaning: 'Request limitation of how we use your data.' },
    ],
    footer: 'To exercise any of these rights, please contact us at supportus@jobxbharat.com. We will respond within 30 days of receiving your request.',
  },
  {
    id: 'data-retention',
    title: '6. Data Retention',
    text: 'We retain your personal data for as long as your account is active or as needed to provide services. You may request deletion of your account and data at any time.',
    bullets: [
      'Account and profile data: Retained for the duration of your account plus 2 years after deletion.',
      'Application history and recruiter activity: Retained for up to 3 years for compliance and dispute resolution.',
      'Financial/transactional data: Retained for 7 years as required under Indian tax laws.',
      'Log files and security records: Retained for up to 12 months.',
    ],
  },
  {
    id: 'cookies',
    title: '7. Cookies and Tracking Technologies',
    text: 'JobxBharat uses cookies and similar tracking technologies to enhance your experience on the platform.',
    items: [
      { label: 'Essential Cookies', text: 'For login authentication and session management.' },
      { label: 'Analytics Cookies', text: 'To understand how users navigate the platform (e.g., Google Analytics).' },
      { label: 'Preference Cookies', text: 'To remember your language, location, and display settings.' },
      { label: 'Marketing Cookies', text: 'For job recommendations and relevant promotions.' },
    ],
    footer: 'You may manage or disable cookies through your browser settings. Please note that disabling essential cookies may affect platform functionality.',
  },
  {
    id: 'data-security',
    title: '8. Data Security',
    text: 'We implement industry-standard security measures to protect your data from unauthorized access, loss, or misuse:',
    bullets: [
      'SSL/TLS encryption for all data transmitted between your browser and our servers.',
      'AES-256 encryption for sensitive data stored in our databases.',
      'Role-based access controls ensuring only authorized personnel access your data.',
      'Regular security audits, penetration testing, and vulnerability assessments.',
      'Two-factor authentication (2FA) available for user accounts.',
    ],
    footer: 'In the event of a data breach that affects your rights and freedoms, we will notify you and the relevant authorities as required by law within 72 hours.',
  },
  {
    id: 'international-transfers',
    title: '9. International Data Transfers',
    text: 'JobxBharat primarily operates within India. In cases where your data is processed or stored outside India (e.g., via international cloud service providers), we ensure that adequate safeguards are in place, including standard contractual clauses, data processing agreements, and compliance with applicable cross-border data transfer regulations.',
  },
  {
    id: 'childrens-privacy',
    title: "10. Children's Privacy",
    text: "JobxBharat is not directed at individuals under 18 years of age. We do not knowingly collect personal data from minors. If we become aware that a minor has created an account, we will promptly delete their information. If you believe a minor has provided us with their data, please contact us immediately at supportus@jobxbharat.com.",
  },
  {
    id: 'third-party-links',
    title: '11. Third-Party Links',
    text: 'Our platform may contain links to third-party websites, employer portals, or external services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies before providing any personal information.',
  },
  {
    id: 'changes',
    title: '12. Changes to This Privacy Policy',
    text: 'We may update this Privacy Policy from time to time. We will notify users of material changes through:',
    bullets: [
      'A prominent notice on the platform\'s homepage.',
      'Email notification to your registered email address.',
      'In-app notifications (where applicable).',
    ],
    footer: 'Your continued use of JobxBharat after such changes constitutes your acceptance of the revised Privacy Policy.',
  },
  {
    id: 'grievance',
    title: '13. Grievance Officer',
    contact: {
      name: 'Grievance Officer, JobxBharat',
      email: 'supportus@jobxbharat.com',
      address: 'India',
      hours: 'Monday to Friday, 10:00 AM – 6:00 PM IST',
      response: '30 days',
    },
  },
];

function BulletList({ items }) {
  return (
    <ul className="space-y-2 mt-3">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 text-sm text-ink-500 leading-relaxed">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-purple mt-2 shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

function LabeledList({ items }) {
  return (
    <ul className="space-y-3 mt-3">
      {items.map((item, i) => (
        <li key={i} className="text-sm leading-relaxed">
          <span className="font-bold text-ink-900">{item.label}: </span>
          <span className="text-ink-500">{item.text}</span>
        </li>
      ))}
    </ul>
  );
}

function RightsTable({ rows }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-secondary-50 text-left">
            <th className="px-5 py-3 font-black text-ink-900 text-xs uppercase tracking-wide w-44">Right</th>
            <th className="px-5 py-3 font-black text-ink-900 text-xs uppercase tracking-wide">What It Means</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-5 py-3 font-semibold text-brand-purple align-top">{row.right}</td>
              <td className="px-5 py-3 text-ink-500">{row.meaning}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ section }) {
  return (
    <div id={section.id} className="scroll-mt-28 pb-10 border-b border-gray-100 last:border-0">
      <h2 className="font-display font-black text-xl md:text-2xl text-ink-900 mb-4">
        {section.title}
      </h2>

      {section.text && <p className="text-sm text-ink-500 leading-relaxed">{section.text}</p>}

      {section.bullets && <BulletList items={section.bullets} />}
      {section.items && <LabeledList items={section.items} />}
      {section.table && <RightsTable rows={section.table} />}

      {section.content && section.content.map((block, i) => (
        <div key={i} className="mt-5">
          {block.sub && (
            <h3 className="font-display font-bold text-base text-ink-900 mb-2">{block.sub}</h3>
          )}
          {block.text && <p className="text-sm text-ink-500 leading-relaxed">{block.text}</p>}
          {block.items && <LabeledList items={block.items} />}
          {block.bullets && <BulletList items={block.bullets} />}
        </div>
      ))}

      {section.footer && (
        <p className="mt-4 text-sm text-ink-500 leading-relaxed italic">{section.footer}</p>
      )}

      {section.contact && (
        <div className="mt-4 p-5 rounded-2xl bg-secondary-50 border border-secondary-100 space-y-2 text-sm">
          <p><span className="font-bold text-ink-900">Name:</span> <span className="text-ink-500">{section.contact.name}</span></p>
          <p>
            <span className="font-bold text-ink-900">Email:</span>{' '}
            <a href={`mailto:${section.contact.email}`} className="text-brand-purple hover:underline">{section.contact.email}</a>
          </p>
          <p><span className="font-bold text-ink-900">Address:</span> <span className="text-ink-500">{section.contact.address}</span></p>
          <p><span className="font-bold text-ink-900">Response Time:</span> <span className="text-ink-500">{section.contact.response}</span></p>
          <p><span className="font-bold text-ink-900">Hours:</span> <span className="text-ink-500">{section.contact.hours}</span></p>
        </div>
      )}
    </div>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-paper text-ink-900">

      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm font-semibold text-ink-500
                       hover:text-ink-900 transition-colors"
          >
            <FaArrowLeft /> Back to Home
          </button>
          <span className="font-brand font-black text-lg text-ink-900">Jobx<span className="text-brand-saffron">Bharat</span></span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-16 lg:grid lg:grid-cols-[260px_1fr] lg:gap-16">

        {/* Sidebar TOC — sticky on desktop */}
        <aside className="hidden lg:block">
          <div className="sticky top-28 space-y-1">
            <p className="text-xs font-black uppercase tracking-widest text-ink-300 mb-4">Contents</p>
            {SECTIONS.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block text-xs font-semibold text-ink-500 hover:text-brand-purple
                           py-1 border-l-2 border-transparent hover:border-brand-purple
                           pl-3 transition-all leading-snug"
              >
                {s.title}
              </a>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main>
          {/* Hero header */}
          <div className="mb-12 pb-10 border-b border-gray-100">
            <span className="text-xs font-black tracking-widest uppercase text-brand-purple mb-3 block">
              Legal
            </span>
            <h1 className="font-display font-black text-4xl md:text-5xl text-ink-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-ink-500 text-sm">
              Effective Date: <strong>January 1, 2026</strong> &nbsp;|&nbsp; Version 1.0
            </p>
            <p className="text-ink-500 text-sm mt-3 leading-relaxed max-w-2xl">
              This Privacy Policy explains how JobxBharat collects, uses, stores, shares, and protects
              your personal information when you access or use our job portal services — whether as a
              Job Seeker or a Recruiter/Employer. By using our Platform, you agree to the practices
              described in this Policy.
            </p>
          </div>

          <div className="space-y-10">
            {SECTIONS.map(s => <Section key={s.id} section={s} />)}
          </div>

          {/* Contact footer strip */}
          <div className="mt-14 p-6 rounded-2xl bg-brand-purple text-white flex flex-col sm:flex-row
                          items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-display font-black text-lg">Questions about your privacy?</p>
              <p className="text-white/70 text-sm mt-1">Our privacy team responds within 30 days.</p>
            </div>
            <a
              href="mailto:supportus@jobxbharat.com"
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-brand-purple
                         font-black text-sm hover:brightness-95 transition-all shrink-0"
            >
              <FaEnvelope /> supportus@jobxbharat.com
            </a>
          </div>
        </main>
      </div>

      <footer className="py-8 border-t border-gray-100 text-center text-xs text-ink-300 bg-white">
        © {new Date().getFullYear()} Jobx<span className="text-brand-saffron">Bharat</span> · All rights reserved ·{' '}
        <a href="/terms-and-conditions" className="hover:text-brand-purple">Terms & Conditions</a>
      </footer>
    </div>
  );
}
